import { useQuery } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { inventoryApi } from 'src/Services/API';
import { InventoryItemDto, InventoryOverviewDto } from 'src/Services/API/generated';

export const inventoryQueryKey = (assetId: string | undefined) => ['inventory', assetId] as const;

export const inventoryItemQueryKey = (assetId: string | undefined, itemId: string | undefined) =>
  ['inventory', assetId, 'item', itemId] as const;

export const inventoryExitedQueryKey = (assetId: string | undefined) =>
  ['inventory', assetId, 'exited'] as const;

/** Objects still in the house, totals (managers only) and the house wear rule. */
const useFetchInventory = () => {
  const assetId = useCurrentAssetId();
  return useQuery<InventoryOverviewDto>({
    queryKey: inventoryQueryKey(assetId),
    queryFn: async () => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerOverview(assetId)).data;
    },
    enabled: !!assetId,
  });
};

export const useFetchInventoryItem = (itemId?: string) => {
  const assetId = useCurrentAssetId();
  return useQuery<InventoryItemDto>({
    queryKey: inventoryItemQueryKey(assetId, itemId),
    queryFn: async () => {
      if (!assetId || !itemId) throw new Error('No assetId or itemId provided');
      return (await inventoryApi.inventoryControllerFindOne(assetId, itemId)).data;
    },
    enabled: !!assetId && !!itemId,
  });
};

export const useFetchInventoryExited = () => {
  const assetId = useCurrentAssetId();
  return useQuery<InventoryItemDto[]>({
    queryKey: inventoryExitedQueryKey(assetId),
    queryFn: async () => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerExited(assetId)).data;
    },
    enabled: !!assetId,
  });
};

export default useFetchInventory;
